import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, makeStyles, Typography } from "@material-ui/core";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";

import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import ChatModal from "./ChatModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  getChatTitle,
  mergeChat,
  mergeMessages,
  sortChats,
  unreadsFor,
} from "./chatUtils";

const NO_MESSAGES = [];

const useStyles = makeStyles((theme) => ({
  root: {
    // o CssBaseline do app fica fora do ThemeProvider: sem isso o texto
    // herda o preto do tema claro no tema escuro
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
    height: "calc(100% - 58px)",
    boxSizing: "border-box",
    padding: theme.spacing(3),
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      gap: 0,
      height: "calc(100% - 56px)",
    },
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  panel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    [theme.breakpoints.down("sm")]: {
      border: 0,
      borderRadius: 0,
    },
  },
  side: {
    width: 340,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    minHeight: 0,
  },
  main: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  mobileFull: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  empty: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.type === "dark" ? "#1A2035" : "#F7F9FC",
  },
  emptyIcon: { fontSize: 56, opacity: 0.3 },
}));

function Chat({ width }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id: routeUuid } = useParams();
  const { user } = useContext(AuthContext);
  const isDesktop = isWidthUp("md", width);

  const [chats, setChats] = useState([]);
  const [chatsPage, setChatsPage] = useState(1);
  const [chatsHasMore, setChatsHasMore] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(true);

  const [messages, setMessages] = useState([]);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadMark, setUnreadMark] = useState({ id: null, count: 0 });
  // conversa dona de `messages`: evita mostrar por um instante as mensagens
  // da conversa anterior ao trocar
  const [messagesChatId, setMessagesChatId] = useState(null);

  // quem está digitando: { [chatId]: { [userId]: nome } }
  const [typing, setTyping] = useState({});
  const typingTimers = useRef({});
  const lastTypingSent = useRef(0);

  const [modal, setModal] = useState({ open: false, type: "new" });
  const [chatToDelete, setChatToDelete] = useState(null);

  const sortedChats = useMemo(() => sortChats(chats), [chats]);
  const currentChat = useMemo(
    () => (routeUuid ? chats.find((c) => c.uuid === routeUuid) : null) || null,
    [chats, routeUuid]
  );
  const currentChatId = currentChat?.id;

  const currentChatIdRef = useRef();
  currentChatIdRef.current = currentChatId;
  const messagesPageRef = useRef(1);

  const upsertChat = useCallback((record) => {
    if (!record?.id) return;
    setChats((prev) => {
      const index = prev.findIndex((c) => c.id === record.id);
      if (index === -1) return [record, ...prev];
      const next = [...prev];
      next[index] = mergeChat(prev[index], record);
      return next;
    });
  }, []);

  // Zera o contador local na hora, sem esperar o socket
  const markAsRead = useCallback(
    (chatId) => {
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId
            ? {
                ...c,
                users: (c.users || []).map((u) =>
                  u.userId === user.id ? { ...u, unreads: 0 } : u
                ),
              }
            : c
        )
      );
      api.post(`/chats/${chatId}/read`, { userId: user.id }).catch(() => {});
    },
    [user.id]
  );

  const loadChats = useCallback(async (page) => {
    setChatsLoading(true);
    try {
      const { data } = await api.get("/chats", { params: { pageNumber: page } });
      setChats((prev) => {
        const next = page === 1 ? [] : [...prev];
        data.records.forEach((record) => {
          const index = next.findIndex((c) => c.id === record.id);
          if (index === -1) next.push(record);
          else next[index] = mergeChat(next[index], record);
        });
        return next;
      });
      setChatsHasMore(data.hasMore);
      setChatsPage(page);
    } catch (err) {
      toastError(err);
    }
    setChatsLoading(false);
  }, []);

  useEffect(() => {
    loadChats(1);
  }, [loadChats]);

  // Conversa aberta pelo link (popover, notificação) que não está na
  // primeira página: continua carregando até achar
  useEffect(() => {
    if (routeUuid && !currentChat && !chatsLoading && chatsHasMore) {
      loadChats(chatsPage + 1);
    }
  }, [routeUuid, currentChat, chatsLoading, chatsHasMore, chatsPage, loadChats]);

  // /chats?nova=1 (botão "Nova" do popover) abre o modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("nova")) {
      setModal({ open: true, type: "new" });
      history.replace(location.pathname);
    }
  }, [location.search, location.pathname, history]);

  // Mensagens da conversa aberta
  useEffect(() => {
    setMessages([]);
    setMessagesHasMore(false);
    setUnreadMark({ id: null, count: 0 });
    setMessagesChatId(currentChatId || null);
    messagesPageRef.current = 1;
    if (!currentChatId) return undefined;

    let cancelled = false;
    const unreads = unreadsFor(currentChat, user.id);

    (async () => {
      setMessagesLoading(true);
      try {
        const { data } = await api.get(`/chats/${currentChatId}/messages`, {
          params: { pageNumber: 1 },
        });
        if (cancelled) return;
        const records = data.records || [];
        setMessages(records);
        setMessagesHasMore(data.hasMore);
        messagesPageRef.current = 2;
        if (unreads > 0 && records.length > 0) {
          const index = Math.max(0, records.length - unreads);
          setUnreadMark({ id: records[index].id, count: unreads });
        }
      } catch (err) {
        if (!cancelled) toastError(err);
      }
      if (!cancelled) setMessagesLoading(false);
    })();

    // sempre: além de zerar não lidas, registra o "visto" das mensagens
    markAsRead(currentChatId);

    return () => {
      cancelled = true;
    };
    // só ao trocar de conversa; currentChat muda a cada evento de socket
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChatId]);

  const loadMoreMessages = async () => {
    const chatId = currentChatId;
    if (!chatId || messagesLoading || !messagesHasMore) return;
    setMessagesLoading(true);
    try {
      const { data } = await api.get(`/chats/${chatId}/messages`, {
        params: { pageNumber: messagesPageRef.current },
      });
      if (chatId === currentChatIdRef.current) {
        messagesPageRef.current += 1;
        setMessages((prev) => mergeMessages(data.records || [], prev));
        setMessagesHasMore(data.hasMore);
      }
    } catch (err) {
      toastError(err);
    }
    setMessagesLoading(false);
  };

  const clearTyping = useCallback((chatId, userId) => {
    const key = `${chatId}:${userId}`;
    clearTimeout(typingTimers.current[key]);
    delete typingTimers.current[key];
    setTyping((prev) => {
      if (!prev[chatId] || !prev[chatId][userId]) return prev;
      const forChat = { ...prev[chatId] };
      delete forChat[userId];
      return { ...prev, [chatId]: forChat };
    });
  }, []);

  useEffect(
    () => () => Object.values(typingTimers.current).forEach(clearTimeout),
    []
  );

  // Tempo real. Um socket só, com setState funcional: os handlers não
  // dependem de cópias antigas da lista.
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    const isMember = (chat) =>
      (chat?.users || []).some((u) => u.userId === user.id);

    socket.on(`company-${companyId}-chat-user-${user.id}`, (data) => {
      if (data.action === "create" || data.action === "update") {
        upsertChat(data.record);
      }
    });

    socket.on(`company-${companyId}-chat-typing`, (data) => {
      if (!data || data.userId === user.id) return;
      const { chatId, userId, name } = data;
      setTyping((prev) => ({
        ...prev,
        [chatId]: { ...(prev[chatId] || {}), [userId]: name || "Alguém" },
      }));
      const key = `${chatId}:${userId}`;
      clearTimeout(typingTimers.current[key]);
      // quem digita avisa a cada ~3s; sem aviso por 5s, some
      typingTimers.current[key] = setTimeout(
        () => clearTyping(chatId, userId),
        5000
      );
    });

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "delete") {
        const deletedId = +data.id;
        setChats((prev) => prev.filter((c) => c.id !== deletedId));
        if (deletedId === currentChatIdRef.current) history.push("/chats");
        return;
      }

      let chat = data.chat;
      if (!chat) return;

      if (!isMember(chat)) {
        // removido do grupo numa edição
        setChats((prev) => prev.filter((c) => c.id !== chat.id));
        if (chat.id === currentChatIdRef.current) history.push("/chats");
        return;
      }

      if (data.action === "new-message" && data.newMessage) {
        const msg = data.newMessage;
        clearTyping(chat.id, msg.senderId);
        if (chat.id === currentChatIdRef.current) {
          setMessages((prev) => mergeMessages(prev, [msg]));
          if (msg.senderId === user.id) {
            setUnreadMark({ id: null, count: 0 });
          } else if (document.visibilityState === "visible") {
            // está com a conversa aberta: não conta como não lida
            chat = {
              ...chat,
              users: chat.users.map((u) =>
                u.userId === user.id ? { ...u, unreads: 0 } : u
              ),
            };
            api
              .post(`/chats/${chat.id}/read`, { userId: user.id })
              .catch(() => {});
          }
        }
      }

      upsertChat(chat);
    });

    return () => {
      socket.disconnect();
    };
  }, [user.id, upsertChat, history, clearTyping]);

  // Voltou para a aba com mensagens acumuladas na conversa aberta
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        currentChat &&
        unreadsFor(currentChat, user.id) > 0
      ) {
        markAsRead(currentChat.id);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [currentChat, user.id, markAsRead]);

  const selectChat = (chat) => {
    if (chat.uuid !== routeUuid) history.push(`/chats/${chat.uuid}`);
  };

  // Rejeita em caso de erro para o campo manter o rascunho
  const sendMessage = async (message, file, ticketId) => {
    if (!currentChatId) return;
    try {
      if (file) {
        const form = new FormData();
        form.append("message", message || "");
        if (ticketId) form.append("ticketId", String(ticketId));
        form.append("media", file, file.name);
        await api.post(`/chats/${currentChatId}/messages`, form);
      } else {
        await api.post(`/chats/${currentChatId}/messages`, {
          message,
          ...(ticketId ? { ticketId } : {}),
        });
      }
      lastTypingSent.current = 0;
    } catch (err) {
      if (err?.response?.status === 413) {
        // barrado antes de chegar à API (ex.: client_max_body_size do nginx)
        toast.error("O servidor recusou o arquivo por ser grande demais.");
      } else {
        toastError(err);
      }
      throw err;
    }
  };

  // Avisa "digitando…" no máximo a cada 3s
  const notifyTyping = () => {
    const now = Date.now();
    if (!currentChatId || now - lastTypingSent.current < 3000) return;
    lastTypingSent.current = now;
    api.post(`/chats/${currentChatId}/typing`).catch(() => {});
  };

  const openTicket = (ticket) => {
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  const typingNamesFor = (chatId) => Object.values(typing[chatId] || {});

  const confirmDelete = async () => {
    if (!chatToDelete) return;
    try {
      await api.delete(`/chats/${chatToDelete.id}`);
    } catch (err) {
      toastError(err);
    }
    setChatToDelete(null);
  };

  const handleSaved = (record, wasEdit) => {
    upsertChat(record);
    if (!wasEdit) history.push(`/chats/${record.uuid}`);
  };

  const openNew = () => setModal({ open: true, type: "new" });

  const messagesReady = messagesChatId === currentChatId;

  const conversation = currentChat ? (
    <ChatMessages
      key={currentChat.id}
      chat={currentChat}
      messages={messagesReady ? messages : NO_MESSAGES}
      hasMore={messagesReady && messagesHasMore}
      loading={messagesLoading || !messagesReady}
      firstUnreadId={messagesReady ? unreadMark.id : null}
      unreadCount={unreadMark.count}
      isMobile={!isDesktop}
      onBack={() => history.push("/chats")}
      onSendMessage={sendMessage}
      onTyping={notifyTyping}
      onOpenTicket={openTicket}
      typingNames={typingNamesFor(currentChat.id)}
      onLoadMore={loadMoreMessages}
      onEdit={() => setModal({ open: true, type: "edit" })}
      onDelete={(chat) => setChatToDelete(chat)}
    />
  ) : null;

  const list = (
    <ChatList
      chats={sortedChats}
      typing={typing}
      currentChatId={currentChatId}
      loading={chatsLoading}
      hasMore={chatsHasMore}
      onLoadMore={() => loadChats(chatsPage + 1)}
      onSelectChat={selectChat}
      onNewChat={openNew}
    />
  );

  return (
    <>
      <ChatModal
        open={modal.open}
        type={modal.type}
        chat={modal.type === "edit" ? currentChat : null}
        chats={chats}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onSaved={handleSaved}
        onOpenExisting={selectChat}
      />
      <ConfirmationModal
        title="Excluir conversa"
        open={!!chatToDelete}
        onClose={() => setChatToDelete(null)}
        onConfirm={confirmDelete}
      >
        {chatToDelete
          ? `A conversa "${getChatTitle(chatToDelete, user.id)}" e todas as mensagens serão apagadas para todos os participantes. Esta ação não pode ser desfeita.`
          : ""}
      </ConfirmationModal>

      <div className={classes.root}>
        {isDesktop && (
          <div className={classes.header}>
            <h1 className={classes.title}>Chat interno</h1>
            <span className={classes.subtitle}>
              Converse com a equipe sem sair do atendimento
            </span>
          </div>
        )}

        <div className={classes.panel}>
          {isDesktop ? (
            <>
              <div className={classes.side}>{list}</div>
              <div className={classes.main}>
                {conversation || (
                  <div className={classes.empty}>
                    <ForumOutlinedIcon className={classes.emptyIcon} />
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      Selecione uma conversa
                    </Typography>
                    <Typography variant="body2">
                      ou comece uma nova com alguém da equipe.
                    </Typography>
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      onClick={openNew}
                      style={{ marginTop: 8, textTransform: "none", fontWeight: 600 }}
                    >
                      Nova conversa
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={classes.mobileFull}>{conversation || list}</div>
          )}
        </div>
      </div>
    </>
  );
}

export default withWidth()(Chat);
